import type { FloorInfo } from '@/services/dto/kiosk-config';
import { useConfigStore } from '@/stores';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Swiper as SwiperClass } from 'swiper';
import type { SwiperRef } from 'swiper/react';

export const useVerticalFloorManagement = (floorsParam: FloorInfo[], amountOfFloorsToShow = 5, selectedFloorName?: string) => {
    const swiperRef = useRef<SwiperRef>(null);
    const [isDisabledPrev, setIsDisabledPrev] = useState(false);
    const [isDisabledNext, setIsDisabledNext] = useState(false);
    const screenMode = useConfigStore((state) => state.config.ui.screenMode);

    const floors = useMemo(() => {
        if (!Array.isArray(floorsParam) || !floorsParam?.length) return [];
        return floorsParam;
    }, [floorsParam]);

    const selectedFloorIndex = useMemo(() => {
        if (!selectedFloorName || !floors.length) return -1;
        const isMatchingFloor = (floor: FloorInfo): boolean => {
            if (!floor?.name) return false;
            if (Array.isArray(floor.name)) {
                return floor.name.some((item) => item?.text === selectedFloorName);
            }
            return Object.values(floor.name).some((name) => name === selectedFloorName);
        };
        const index = floors.findIndex(isMatchingFloor);
        return index >= 0 ? index : -1;
    }, [selectedFloorName, floors]);

    const shouldScroll = useMemo(() => floors.length > amountOfFloorsToShow, [floors, amountOfFloorsToShow]);

    const handleDisableNext = useCallback((disabled: boolean) => {
        setIsDisabledNext(disabled);
    }, []);

    const handleDisablePrev = useCallback((disabled: boolean) => {
        setIsDisabledPrev(disabled);
    }, []);

    const handleScrollUp = useCallback(() => {
        if (!swiperRef?.current) return;
        swiperRef.current?.swiper?.slidePrev?.();
    }, []);

    const handleScrollDown = useCallback(() => {
        if (!swiperRef?.current) return;
        swiperRef.current?.swiper?.slideNext?.();
    }, []);

    const scrollToSelectedFloor = useCallback((swiper: SwiperClass) => {
        if (selectedFloorIndex < 0) return;
        swiper.slideTo(selectedFloorIndex, 0);
        setIsDisabledPrev(swiper.isBeginning);
        setIsDisabledNext(swiper.isEnd);
    }, [selectedFloorIndex]);

    const handleSwiperInit = useCallback((swiper: SwiperClass) => {
        scrollToSelectedFloor(swiper);
    }, [scrollToSelectedFloor]);

    useEffect(() => {
        if (selectedFloorIndex >= 0 && swiperRef.current?.swiper) {
            scrollToSelectedFloor(swiperRef.current.swiper);
        }
    }, [selectedFloorIndex, screenMode, scrollToSelectedFloor]);

    return {
        isDisabledPrev,
        isDisabledNext,
        shouldScroll,
        swiperRef,
        handleSwiperInit,
        handleScrollUp,
        handleScrollDown,
        handleDisableNext,
        handleDisablePrev,
    };
};
